<script setup lang="ts">
import type { Component } from 'vue'
import { formatValue } from '@forgd/formatter'
import { Badge } from '@forgd/ui-kit/badge'
import { Card } from '@forgd/ui-kit/card'
import {
  ArrowsRightLeftIcon,
  ChartBarIcon,
  FlagIcon,
  PresentationChartBarIcon,
  QuestionMarkCircleIcon,
  Square3Stack3DIcon,
} from '@heroicons/vue/24/outline'

const props = defineProps<{
  type: 'aggregated' | 'kpi' | 'depth' | 'spread' | 'volume'
  percentile?: number | null
  value?: number | null
}>()

const formattedPercentile = computed(() => formatValue(props.percentile, 'percent'))
const formattedRank = computed(() => formatValue(props.value, 'number'))

const labels = computed<{ title: string, icon: Component, description: string }>(() => {
  switch (props.type) {
    case 'aggregated':
      return {
        title: 'Aggregated Leaderboard',
        icon: Square3Stack3DIcon,
        description: `Outperformed ${formattedPercentile.value} of their peers across all major KPIs.`,
      }
    case 'depth':
      return {
        title: 'Depth Leaderboard',
        icon: ChartBarIcon,
        description: `Outperformed ${formattedPercentile.value} of their peers for liquidity provision.`,
      }
    case 'volume':
      return {
        title: 'Volume Leaderboard',
        icon: PresentationChartBarIcon,
        description: `Outperformed ${formattedPercentile.value} of their peers for trade volume. `,
      }
    case 'spread':
      return {
        title: 'Spread Leaderboard',
        icon: ArrowsRightLeftIcon,
        description: `Outperformed ${formattedPercentile.value} of their peers for top-of-book spreads.`,
      }
    case 'kpi':
      return {
        title: 'KPI Adherence Leaderboard',
        icon: FlagIcon,
        description: `Outperformed ${formattedPercentile.value} of their peers for uptime across critical KPIs.`,
      }
    default:
      return {
        title: 'N/A',
        icon: QuestionMarkCircleIcon,
        description: 'N/A',
      }
  }
})
</script>

<template>
  <Card v-if="percentile != null || (value && value > 0)">
    <div class="p-3 flex flex-col gap-3 md:flex-row md:justify-between md:gap-0">
      <div class="flex flex-1 flex-col gap-1">
        <div class="flex flex-row gap-2 items-center">
          <component :is="labels.icon" class="size-4 shrink-0" aria-hidden="true" />
          <div class="font-semibold text-sm text-primal">
            {{ labels.title }}
          </div>
        </div>
        <div class="text-sm text-secondary">
          {{ labels.description }}
        </div>
      </div>
      <div class="flex flex-row gap-2 md:flex-1 md:flex-col md:items-end">
        <Badge variant="gray">
          Percentile: {{ formattedPercentile }}
        </Badge>
        <Badge variant="gray">
          Rank: {{ formattedRank }}
        </Badge>
      </div>
    </div>
  </Card>
</template>
